import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiPOST } from "../../utilities/apiHelpers";
import { toast } from "react-toastify";
import { MdClose, MdArrowBack, MdVisibility, MdVisibilityOff } from "react-icons/md";

const ForgetPassword = ({ show, handleClose }) => {
  const navigate = useNavigate();

  // States
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Email, 2: OTP, 3: Reset Password
  const [emailError, setEmailError] = useState(""); // 1. Added error state
  const [showConfirmPass, setShowConfirmPass] = useState(false);

  const [timer, setTimer] = useState(300); // 5 minutes in seconds

  useEffect(() => {
    if (timer === 0) return;
    const interval = setInterval(() => {
      setTimer((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [timer]);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  // Reset state when modal is closed
  useEffect(() => {
    if (!show) {
      setEmail("");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setStep(1);
      setShowPass(false);
      if (!show) {
        setEmail("");
        setEmailError(""); // Clear error on close
        // ... other resets
      }
    }
  }, [show]);

  // States

  // ... other states

  // Reset state when modal is closed
  useEffect(() => {

  }, [show]);

  // STEP 1: Request OTP
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      setEmailError("Email address is required");
      return;
    }
    try {
      setLoading(true);
      const resp = await apiPOST("/v1/auth/forgot-password", { email });
      if (resp?.data?.status || resp?.status === 200) {
        toast.success("OTP sent to your email!");
        setStep(2);
        setTimer(300);
      } else {
        toast.error(resp?.data?.message || "Email not found");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // STEP 2: Verify OTP
  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    if (otp.length < 4) {
      toast.error("Please enter the full 4-digit code");
      return;
    }
    try {
      setLoading(true);
      const resp = await apiPOST("/v1/auth/forgot-verify-otp", { email, otp });
      if (resp?.data?.status || resp?.status === 200) {
        toast.success("OTP Verified Successfully!");
        setStep(3);
      } else {
        toast.error(resp?.data?.message || "Invalid OTP");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  // STEP 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      const payload = { email, password: newPassword };
      const resp = await apiPOST("/v1/auth/reset-password", payload);

      if (resp?.data?.status || resp?.status === 200) {
        toast.success("Password updated successfully!");
        handleClose();
        navigate("/login");
      } else {
        toast.error(resp?.data?.message || "Failed to reset password");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Helper for OTP Input Logic
  const handleOtpChange = (value, index) => {
    const val = value.replace(/\D/g, ""); // Only numbers
    if (!val && value !== "") return; // Prevent non-numeric

    let otpArray = otp.split("");
    otpArray[index] = val.slice(-1); // Take only the last digit typed
    const newOtp = otpArray.join("");
    setOtp(newOtp);

    // Auto-focus next
    if (val && index < 3) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!otp[index] && index > 0) {
        // If current is empty, move back and clear previous
        document.getElementById(`otp-${index - 1}`).focus();
        let otpArray = otp.split("");
        otpArray[index - 1] = "";
        setOtp(otpArray.join(""));
      } else {
        // Clear current digit
        let otpArray = otp.split("");
        otpArray[index] = "";
        setOtp(otpArray.join(""));
      }
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-opacity">
      <div className="relative w-full max-w-md bg-white shadow-2xl rounded-2xl  animate-in fade-in zoom-in-95 duration-300">

        {/* Top Navigation Row */}
        <div className="flex items-center justify-between p-6">
          <div>
            <h4 className="text-2xl font-bold">
              {step === 1 && "Forgot Password"}
              {step === 2 && "Verify OTP"}
              {step === 3 && "Reset Password"}
            </h4>
          </div>
          <div className="flex gap-2">
            {step > 1 ? (
              <button
                onClick={() => setStep(step - 1)}
                className="flex items-center gap-1 px-3 py-1.5 text-sm font-bold text-white bg-[#095D7E] rounded-lg hover:bg-[#074b66] shadow-sm transition-all active:scale-95"
              >
                <MdArrowBack size={18} />
                <span>Back</span>
              </button>
            ) : (
              <div />
            )}

            <button
              onClick={handleClose}
              className="p-1.5 text-white bg-[#FF5252] rounded-lg hover:bg-[#D32F2F] shadow-sm transition-all active:scale-95"
              title="Close"
            >
              <MdClose size={20} />
            </button>
          </div>
        </div>
        <hr />

        <div className="flex flex-col px-8 py-6">

          <p className="text-sm text-gray-500 mb-8 leading-relaxed">
            {step === 1 && "No worries! Enter your email and we'll send you a 4-digit code."}
            {step === 2 && `We've sent a 4-digit code to ${email}`}
            {step === 3 && "Almost there. Create a strong new password for your account."}
          </p>

          <form
            onSubmit={
              step === 1 ? handleEmailSubmit :
                step === 2 ? handleOtpSubmit :
                  handleResetPassword
            }
            className="space-y-6"
          >
            {/* Step 1: Email */}
            {step === 1 && (
              <div>
                <label className="block text-gray-700 text-sm font-semibold mb-2">Email address</label>
                <input
                  type="email"
                  // 4. Removed 'required', 'onInvalid', and 'onInput'
                  className={`w-full px-4 py-3 rounded-xl bg-gray-50 border outline-none transition-all text-sm ${emailError ? "border-red-500 focus:ring-red-500/20" : "border-gray-200 focus:ring-[#095D7E]/20 focus:border-[#095D7E]"
                    }`}
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError(""); // Clear error while typing
                  }}
                />

                {/* 5. Custom Error Message Display */}
                {emailError && (
                  <p className="mt-1 text-xs text-red-500 font-medium animate-in fade-in slide-in-from-top-1">
                    {emailError}
                  </p>
                )}
              </div>
            )}

            {/* Step 2: 4-Digit OTP Grid */}
            {step === 2 && (
              <div className="flex flex-col items-center">
                <label className="text-gray-700 text-sm font-semibold mb-6">Verification Code</label>
                <div className="flex gap-3 justify-center mb-4">
                  {[0, 1, 2, 3].map((index) => (
                    <input
                      key={index}
                      id={`otp-${index}`}
                      type="text"
                      maxLength={1}
                      value={otp[index] || ""}
                      onChange={(e) => handleOtpChange(e.target.value, index)}
                      onKeyDown={(e) => handleKeyDown(e, index)}
                      className="w-14 h-16 text-center text-2xl font-bold bg-gray-50 border-2 border-gray-200 rounded-xl outline-none focus:border-[#095D7E] focus:ring-4 focus:ring-[#095D7E]/10 transition-all text-[#095D7E]"
                      placeholder="-"
                    />
                  ))}
                </div>
                {timer === 0 ? (
                  <button
                    type="button"
                    disabled={loading}
                    onClick={handleEmailSubmit}
                    className="mt-4 text-xs font-semibold text-[#095D7E] hover:underline disabled:text-gray-400 disabled:no-underline flex items-center gap-2"
                  >
                    {loading ? (
                      <>
                        <span className="w-2 h-2 border border-[#095D7E] border-t-transparent rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : (
                      "Resend Code"
                    )}
                  </button>
                ) : (
                  <p className="text-sm text-gray-500 mt-4">
                    OTP expires in <span className="font-medium text-[#095D7E]">{formatTime(timer)}</span>
                  </p>
                )}
                {/* <button
                  type="button"
                  disabled={loading}
                  onClick={handleEmailSubmit} // Re-uses the same logic from Step 1
                  className="mt-4 text-xs font-semibold text-[#095D7E] hover:underline disabled:text-gray-400 disabled:no-underline flex items-center gap-2"
                >
                  {loading ? (
                    <>
                      <span className="w-2 h-2 border border-[#095D7E] border-t-transparent rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    "Resend Code"
                  )}
                </button> */}
              </div>
            )}

            {/* Step 3: Password Update */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="relative">
                  <label className="block text-gray-700 text-sm font-semibold mb-2">New Password</label>
                  <input
                    type={showPass ? "text" : "password"}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 outline-none focus:border-[#095D7E] transition-all text-sm"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
                    onClick={() => setShowPass(!showPass)}
                  >
                    {showPass ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                  </button>
                </div>

                <div className="relative">
                  <label className="block text-gray-700 text-sm font-semibold mb-2">Confirm New Password</label>
                  <input
                    type={showConfirmPass ? "text" : "password"}
                    required
                    className="w-full px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 outline-none focus:border-[#095D7E] transition-all text-sm"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute right-3 top-[38px] text-gray-400 hover:text-gray-600"
                    onClick={() => setShowConfirmPass(!showConfirmPass)}
                  >
                    {showConfirmPass ? <MdVisibilityOff size={20} /> : <MdVisibility size={20} />}
                  </button>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 mt-2 text-sm text-white font-bold bg-[#095D7E] rounded-xl hover:bg-[#074a66] shadow-lg shadow-[#095D7E]/20 transition-all transform active:scale-[0.98] disabled:opacity-70 disabled:pointer-events-none"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Processing...
                </span>
              ) : (
                step === 1 ? "Send Code" : step === 2 ? "Verify Code" : "Update Password"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;